import { Check, Download, Inbox as InboxIcon, RotateCcw, ShieldAlert, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchBlobUrl, get, post, toQuery } from '../api';
import { useAuth } from '../auth';
import { useAsk } from '../components/Ask';
import { Empty, ErrorBox, Loading, Spinner } from '../components/base';
import { formatDateTime } from '../formats';

/**
 * The Inbox module: what the product's forms collect, in one list the
 * client's people work through.
 *
 * Built for Peak Pro Project (2026-09-27): their website's registrations were
 * emailed to two inboxes and stored nowhere, and the client wanted one place to
 * see and handle them without snip.work copied on every athlete's email. The
 * list is generic - a form name, the fields it sent, a status - so the next
 * product's forms land here without a new screen.
 *
 * Everything shown is the client's own end users' data (name, email, whatever
 * the form asked), so nothing here is decorative: a row is a person waiting
 * for an answer.
 */

type Status = 'new' | 'in_progress' | 'done' | 'spam';

interface Row {
  id: string;
  form: string;
  page: string | null;
  name: string | null;
  email: string | null;
  programme: string | null;
  fields: Record<string, string>;
  status: Status;
  note: string | null;
  handled_by_name: string | null;
  handled_at: string | null;
  created_at: string;
}

interface InboxData {
  counts: Record<Status, number>;
  total: number;
  offset: number;
  page_size: number;
  rows: Row[];
}

const STATUS: { key: Status | 'all'; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'done', label: 'Done' },
  { key: 'spam', label: 'Spam' },
  { key: 'all', label: 'All' },
];

const STATUS_LABEL: Record<Status, string> = {
  new: 'New', in_progress: 'In progress', done: 'Done', spam: 'Spam',
};

const STATUS_STYLE: Record<Status, string> = {
  new: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  in_progress: 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200',
  done: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
  spam: 'bg-paper-200 text-ink-600 dark:bg-ink-700 dark:text-paper-300',
};

/** "strength-8" reads as "strength 8"; the product's own labels are not known here. */
const humanise = (value: string) => value.replace(/[-_]+/g, ' ');

export default function Inbox({ productId }: { productId: string }) {
  const { user } = useAuth();
  const { ask } = useAsk();
  const [filter, setFilter] = useState<Status | 'all'>('new');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<InboxData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    setData(null);
    get<InboxData>(`/modules/${productId}/inbox${toQuery({
      status: filter === 'all' ? undefined : filter, offset,
    })}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load.'));
  }, [productId, filter, offset]);

  useEffect(load, [load]);

  async function setStatus(row: Row, status: Status) {
    // Done is where a note earns its keep: what was done, so the next person
    // does not have to ask. Optional; an empty prompt keeps whatever is there.
    let note: string | null | undefined;
    if (status === 'done') {
      const answer = await ask({
        title: `Mark ${row.name ?? row.email ?? 'this'} as done?`,
        field: { label: 'A note for the record (optional)', initial: row.note ?? '', multiline: true },
        confirmLabel: 'Mark done',
      });
      if (answer === null) return;
      note = answer || undefined;
    }
    setBusy(row.id);
    setError(null);
    try {
      await post(`/modules/${productId}/inbox/${row.id}/status`, { status, ...(note ? { note } : {}) });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  async function remove(row: Row) {
    const reason = await ask({
      title: `Delete ${row.name ?? row.email ?? 'this submission'} for good?`,
      field: { label: 'Why (kept in the audit trail)', required: true },
      confirmLabel: 'Delete',
      danger: true,
    });
    if (reason === null) return;
    setBusy(row.id);
    setError(null);
    try {
      await post(`/modules/${productId}/inbox/${row.id}/delete`, { reason });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Fetched with the token and handed to the browser as a download, the same
   * way the waitlist export works: an <a href> cannot carry the bearer token.
   */
  async function exportCsv() {
    setBusy('csv');
    setError(null);
    try {
      const url = await fetchBlobUrl(
        `/modules/${productId}/inbox.csv${toQuery({ status: filter === 'all' ? undefined : filter })}`,
        'Could not build the file.');
      const a = document.createElement('a');
      a.href = url;
      a.download = `inbox-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the file.');
    } finally {
      setBusy(null);
    }
  }

  if (error && !data) return <ErrorBox message={error} onRetry={load} />;
  if (!data) return <Loading />;

  const waiting = data.counts.new + data.counts.in_progress;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-600 dark:text-paper-300">
          {waiting === 0
            ? 'Nothing waiting. Everything that came in has been handled.'
            : `${waiting} waiting for an answer${data.counts.new ? `, ${data.counts.new} not yet looked at` : ''}.`}
          {' '}Everything sent through the website's forms lands here.
        </p>
        <button type="button" className="btn-secondary text-xs" disabled={busy === 'csv' || data.total === 0}
          onClick={exportCsv}>
          {busy === 'csv' ? <Spinner className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" aria-hidden />}
          Export CSV
        </button>
      </div>

      {user?.role === 'owner' && (
        <p className="text-xs text-ink-500 dark:text-ink-400">
          The product posts to this inbox with the key under Admin &gt; Products &gt; Edit.
        </p>
      )}

      <div role="tablist" aria-label="Status" className="flex flex-wrap gap-2">
        {STATUS.map((s) => {
          const n = s.key === 'all' ? data.counts.new + data.counts.in_progress + data.counts.done + data.counts.spam
            : data.counts[s.key];
          const active = filter === s.key;
          return (
            <button
              key={s.key} type="button" role="tab" aria-selected={active}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition
                ${active
                  ? 'border-ink-900 bg-ink-900 text-paper-50 dark:border-paper-100 dark:bg-paper-100 dark:text-ink-900'
                  : 'border-paper-300 text-ink-600 hover:border-ink-400 dark:border-ink-600 dark:text-paper-300'}`}
              onClick={() => { setFilter(s.key); setOffset(0); }}
            >
              {s.label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      {error && <ErrorBox message={error} />}

      {data.rows.length === 0 ? (
        <div className="card">
          <Empty
            title={filter === 'new' ? 'Nothing new' : 'Nothing here'}
            description={filter === 'new'
              ? 'New submissions appear here the moment the website receives them.'
              : 'No submissions with this status.'}
          />
        </div>
      ) : (
        <ul className="divide-y divide-paper-200 rounded-xl border border-paper-200 bg-white dark:divide-ink-700 dark:border-ink-700 dark:bg-ink-800">
          {data.rows.map((row) => {
            const expanded = open === row.id;
            const extras = Object.entries(row.fields)
              .filter(([k]) => !['name', 'email', 'programme'].includes(k));
            return (
              <li key={row.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    aria-expanded={expanded}
                    onClick={() => setOpen(expanded ? null : row.id)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`badge ${STATUS_STYLE[row.status]}`}>{STATUS_LABEL[row.status]}</span>
                      <span className="font-medium">{row.name ?? row.email ?? 'No name given'}</span>
                      {row.programme && (
                        <span className="text-sm text-ink-600 dark:text-paper-300">{humanise(row.programme)}</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                      {humanise(row.form)} · {formatDateTime(row.created_at)}
                      {row.handled_by_name && row.handled_at
                        ? ` · ${STATUS_LABEL[row.status].toLowerCase()} by ${row.handled_by_name}, ${formatDateTime(row.handled_at)}`
                        : ''}
                    </p>
                  </button>

                  <div className="flex flex-wrap gap-1.5">
                    {row.email && (
                      // Encoded: the address passed ingest's shape check, but a
                      // "?" or "&" is legal in a local part and would otherwise
                      // become mailto parameters (a cc: of the sender's choosing).
                      <a className="btn-secondary text-xs" href={`mailto:${encodeURIComponent(row.email)}`}>Reply</a>
                    )}
                    {row.status === 'new' && (
                      <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                        onClick={() => setStatus(row, 'in_progress')}>
                        Start
                      </button>
                    )}
                    {row.status !== 'done' && row.status !== 'spam' && (
                      <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                        onClick={() => setStatus(row, 'done')}>
                        {busy === row.id ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                        Done
                      </button>
                    )}
                    {row.status !== 'spam' && (
                      <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                        title="Not a real person"
                        onClick={() => setStatus(row, 'spam')}>
                        <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> Spam
                      </button>
                    )}
                    {(row.status === 'done' || row.status === 'spam') && (
                      <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                        onClick={() => setStatus(row, 'new')}>
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reopen
                      </button>
                    )}
                    <button type="button" className="btn-secondary text-xs" disabled={busy === row.id}
                      title="Delete for good (needs a reason)"
                      onClick={() => remove(row)}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </div>

                {expanded && (
                  <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                    {row.email && (
                      <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Email</dt>
                        <dd className="break-all">{row.email}</dd></div>
                    )}
                    {row.programme && (
                      <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Programme</dt>
                        <dd>{row.programme}</dd></div>
                    )}
                    {extras.map(([k, v]) => (
                      <div key={k} className={v.length > 80 ? 'sm:col-span-2' : ''}>
                        <dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">{humanise(k)}</dt>
                        <dd className="whitespace-pre-wrap break-words">{v}</dd>
                      </div>
                    ))}
                    {row.page && (
                      <div><dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Sent from</dt>
                        <dd className="font-mono text-xs">{row.page}</dd></div>
                    )}
                    {row.note && (
                      <div className="sm:col-span-2">
                        <dt className="text-xs uppercase tracking-wide text-ink-500 dark:text-ink-400">Note</dt>
                        <dd className="whitespace-pre-wrap">{row.note}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {data.total > data.page_size && (
        <div className="flex items-center justify-between text-sm">
          <button type="button" className="btn-secondary text-xs" disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - data.page_size))}>Newer</button>
          <span className="text-ink-500 dark:text-ink-400">
            {offset + 1}-{Math.min(offset + data.page_size, data.total)} of {data.total}
          </span>
          <button type="button" className="btn-secondary text-xs" disabled={offset + data.page_size >= data.total}
            onClick={() => setOffset(offset + data.page_size)}>Older</button>
        </div>
      )}

      {data.total === 0 && filter === 'all' && (
        <p className="flex items-center gap-2 text-xs text-ink-500 dark:text-ink-400">
          <InboxIcon className="h-3.5 w-3.5" aria-hidden /> No submissions have reached this inbox yet.
        </p>
      )}
    </div>
  );
}
